import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {PolicyWording} from '../../shared/models/policy.model';
import {PolicyStore} from '../state/policy.store';
import {PolicyWordingStore} from '../state/policy-wording.store';
import {PolicyScheduleStore} from '../state/policy-schedule.store';
import {API_PATH, VapsStatus} from '../../shared/constants';
import {arrayUpdate} from '@datorama/akita';
import {GlobalQuestionService} from '../../shared/services/global-question.service';
import {GetPoliciesRequest} from '../../shared/models/get-policies-request.model';
import {UserStore} from '../../authentication/state/user.store';

let headers = new HttpHeaders()
  .set('content-type', 'application/json')
  .set('Access-Control-Allow-Origin', '*')
  .set('Accept', 'application/json');

@Injectable({
  providedIn: 'root'
})
export class PolicyService {

  policies!: Observable<any>;
  overView: any;
  addOn: any;
  resource: any;
  insuredItemsDetail: any;
  insureItems: any;
  getPolicySubject: BehaviorSubject<boolean>;
  getPolicyObservable: Observable<boolean>;
  quoteScheduleTotalSubject = new BehaviorSubject('');
  quoteScheduleTotalObservable = this.quoteScheduleTotalSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private policyStore: PolicyStore,
    private policyWordingStore: PolicyWordingStore,
    private policyScheduleStore: PolicyScheduleStore,
    private questionService: GlobalQuestionService,
    private userStore: UserStore
  ) {
    this.policies = new Observable<any>();
    this.getPolicySubject = new BehaviorSubject<boolean>(false);
    this.getPolicyObservable = this.getPolicySubject.asObservable();
  }

  setActive(id: any): void {
    // console.log("Active ID:: " + id);
    this.policyStore.setActive(id);
  }

  setQuoteScheduleTotal(value: any): void {
    this.quoteScheduleTotalSubject.next(value);
  }

  setGetPolicyObservable(value: boolean): void {
    this.getPolicySubject.next(value);
  }

  getPolicies2(request: GetPoliciesRequest): Observable<any> {
    const parameters = new Map<string, string>();
    parameters.set('person_id', request?.person_id?.toString());

    if (request?.broker_id) {
      parameters.set('broker_id', request?.broker_id?.toString());
    }
    if (request?.policy_id) {
      parameters.set('policy_id', request?.policy_id);
    }
    if (request?.page_size) {
      parameters.set('page_size', request?.page_size?.toString());
    }
    if (request?.page) {
      parameters.set('page', request?.page?.toString());
    }
    if (request?.brokerage_id) {
      parameters.set('brokerage_id', request?.brokerage_id?.toString());
    }
    if (request?.status) {
      parameters.set('status', request?.status);
    }
    if (request?.broker) {
      parameters.set('broker', request?.broker);
    }
    if (request?.product) {
      parameters.set('product', request?.product);
    }
    if (request?.search_query) {
      parameters.set('search_query', request?.search_query);
    }
    if (request?.sort_field) {
      parameters.set('sort_field', request?.sort_field);
    }
    if (request?.sort_order) {
      parameters.set('sort_order', request?.sort_order);
    }

    return this.questionService.runCommandNew('getPolicies2', parameters);
  }

  getPolicies(personId: number, brokerId: number, policy_id?: string): Observable<any> {
    this.policyStore.reset();
    return this.httpClient.get(`${API_PATH.POLICIES}/${personId}/${brokerId}/policy_id${policy_id ? '?policy_id=' + policy_id : ''}`);
  }

  cancelPolicy(policyId: string, itemId: number, classId: number, vapId: number): void {
    this.httpClient.get(`${API_PATH.CANCEL_POLICY}/${policyId}/${itemId}/${classId}/${vapId}`, {
      headers,
      responseType: 'json'
    })?.subscribe((response: any) => {

    });
  }

  getQuestionGrouping(categoryId: number, sessionId: string): Observable<any> {
    headers = new HttpHeaders().set('responseType', 'json');
    return this.httpClient.get(`${API_PATH.GET_QUESTION_GROUPING}/${categoryId}/${sessionId}`, {headers});
  }

  getPolicyWording(policyId: string): void {
    this.httpClient.get(`${API_PATH.POLICIY_WORDING}/${policyId}`, {headers, responseType: 'json'})?.subscribe((wording: any) => {
      const distinctList: string[] = [];

      wording.forEach((item: PolicyWording) => {
        if (!distinctList.find(val => val === item.section_name)) {
          distinctList.push(item.section_name);
        }
      });

      this.policyWordingStore.set(wording);
      this.policyWordingStore.update({distinctSectionList: distinctList});
    });
  }

  getPolicySchedule(policyId: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.POLICIY_SCHEDULE}/${policyId}`, {headers, responseType: 'json'});
  }

  getPolicyQuoteSchedule(quote_id: string, product_id: string = ''): Observable<any[]> {
    const parameters = new Map<string, string>();
    parameters.set('quote_id', quote_id);
    parameters.set('product_id_input', product_id);
    return this.questionService.runCommandNew('getQuoteSchedule', parameters, true);
  }

  saveProfileDetails(person: any, personId: number, policyId: string): Observable<any> {
    person.person_id = personId;
    person.policy_id = policyId;
    const requestBody = person;
    return this.httpClient.post(API_PATH.SAVE_PROFILE, requestBody);
  }

  saveAnswer(session_id: string, question_id: number, class_id: number, item_id: string, answer: string, answer_id: number, flow_id: number = 0): Observable<any> {
    // console.log(`SessionID:: ${session_id}, Answer:: ${answer}`);
    const params = new HttpParams()
      .set('session_id', session_id)
      .set('answer', answer)
      .set('question_id', question_id.toString())
      .set('class_id', class_id.toString())
      .set('item_id', item_id)
      .set('answer_id', answer_id.toString())
      .set('flow_id', flow_id.toString());
    return this.httpClient.get(`${API_PATH.SAVE_ANSWER}`, {headers, params});
    // return this.httpClient.get(`${API_PATH.SAVE_ANSWER}/${session_id}/${answer}/${question_id}/${class_id}/${item_id}/${answer_id}/${flow_id}`, {headers});
  }

  removeAnswer(session_id: string, question_id: number, class_id: number, item_id: string, answer: string, answer_id: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.REMOVE_ANSWER}/${session_id}/${question_id}/${class_id}/${item_id}/${answer}/${answer_id}`, {headers});
  }

  editPolicy(policy_id: string, curr_session_id: string): Observable<any> {
    const user_id = this.userStore.getValue().person_id;
    return this.httpClient.get(`${API_PATH.EDIT_POLICY}/${policy_id}/${curr_session_id}/${user_id}`, {headers});
  }

  updatePolicy(new_session_id: string, mode: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.UPDATE_POLICY}/${new_session_id}/${mode}`, {headers});
  }

  getAnswersFilter(question_id: number, session_id: string, item_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_ANSWERS_FILTERS}/${question_id}/${session_id}/${item_id}`, {headers});
  }

  getInsurerRating(category_id: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_INSURER_RATING}/${category_id}`, {headers});
  }

  getAgeLicenseYears(session_id: string, item_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_AGE_LICENSE_YEARS}/${session_id}/${item_id}`, {headers});
  }

  getVapQuestions(session_id: string, vap_id: number, category_id: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_VAP_QUESTIONS}/${session_id}/${vap_id}/${category_id}`, {headers});
  }

  getVapPremium(session_id: string, vap_id: number, item_id: string, product_id: number, category_id: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_VAP_PREMIUM}/${session_id}/${vap_id}/${item_id}/${product_id}/${category_id}`, {headers});
  }

  calcPremDiff(session_id: string, as_at_date: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.CALC_PREM_DIFF}/${session_id}/${as_at_date}`, {headers});
  }

  collateAddons(session_id: string, product_id: number, group_id: number, category_id: number, as_at_date: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.COLLATE_ADDONS}/${session_id}/${product_id}/${group_id}/${category_id}/${as_at_date}`, {headers});
  }

  checkPolicyChanges(new_session_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.CHECK_POLICY_CHANGES}/${new_session_id}`, {headers});
  }

  savePolicyUpdate(session_id: string, status_id: number, class_id: number, item_id: string, begin_date: string, end_date: string, vaps_id?: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.SAVE_POLICY_UPDATE}/${session_id}/${status_id}/${class_id}/${item_id}/${begin_date}/${end_date}/vaps_id${vaps_id ? ('?vaps_id=' + vaps_id) : ''}`, {headers});
  }

  removePolicyUpdate(session_id: string, class_id: number, item_id: string, sub_id: number, status_id: number, vaps_id?: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.REMOVE_POLICY_UPDATE}/${session_id}/${class_id}/${item_id}/${sub_id}/${status_id}/vaps_id${vaps_id ? ('?vaps_id=' + vaps_id) : ''}`, {headers});
  }

  submitPolicyUpdates(new_session_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.SUBMIT_POLICY_UPDATES}/${new_session_id}`, {headers});
  }

  savePolicyVapsUpdate(session_id: string, status_id: number, vaps_id: number, item_id: string, begin_date: string, end_date: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.SAVE_POLICY_VAPS_UPDATE}/${session_id}/${status_id}/${vaps_id}/${item_id}/${begin_date}/${end_date}`, {headers});
  }

  removePolicyVapsUpdate(session_id: string, vaps_id: number, class_id: number, item_id: string, sub_id: number): Observable<any> {
    return this.httpClient.get(`${API_PATH.SAVE_POLICY_VAPS_UPDATE}/${session_id}/${vaps_id}/${class_id}/${item_id}/${sub_id}`, {headers});
  }

  getSummaryOfChanges(new_session_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_SUMMARY_OF_CHANGES}/${new_session_id}`, {headers});
  }

  saveVapStatusToStore(policyParam, vapParam, newVapStatus: VapsStatus): void {
    this.policyStore.update(policyParam.policy_id, ({included}) => ({
      included: arrayUpdate(included, (vap: any) => (vap.product_vaps_id === vapParam.product_vaps_id), {vaps_status: newVapStatus})
    }));
  }

  getTranscript(policy_id: string, session_id: string): Observable<any> {
    return this.httpClient.get(`${API_PATH.GET_TRANSCRIPT}/${policy_id}/${session_id}`, {headers});
  }
}
